import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, ArrowRight } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const QUICK_REPLIES = [
  { label: 'Was kostet es?', msg: 'Was kostet CoachDesk365?' },
  { label: 'Funktionen', msg: 'Welche Funktionen hat CoachDesk365?' },
  { label: 'DSGVO?', msg: 'Ist CoachDesk365 DSGVO-konform?' },
  { label: 'Testen', msg: 'Kann ich CoachDesk365 kostenlos testen?' },
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: 'Hallo! Haben Sie eine Frage zu CoachDesk365? Ich helfe gerne – ob zu Funktionen, Preisen oder dem kostenlosen Test.' },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const messagesRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const exchangeCount = useRef(0);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, loading]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;
    const userMsg: Message = { role: 'user', content: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setShowQuickReplies(false);
    setLoading(true);
    exchangeCount.current++;

    try {
      const res = await fetch(`${API_BASE}/public/chatbot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: text.trim(),
          history: messages.slice(-6).map(m => ({ role: m.role, content: m.content })),
          page: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (data.reply) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
        exchangeCount.current++;
        if (exchangeCount.current >= 5 && !showEmailCapture && !emailSent) {
          setShowEmailCapture(true);
        }
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Entschuldigung, da ist etwas schiefgelaufen. Schreiben Sie uns gerne an service@coachdesk365.com.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <>
      {/* Trigger Button */}
      <button
        onClick={() => setOpen(!open)}
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          width: 56, height: 56, borderRadius: 16,
          background: 'var(--primary, #6f3df4)', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 24px rgba(111,61,244,.3)',
          transition: 'transform .2s',
          color: '#fff',
        }}
        onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.05)')}
        onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
        aria-label="Chat öffnen"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {/* Chat Window */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 92, right: 24, zIndex: 9998,
          width: 'min(380px, calc(100vw - 32px))',
          maxHeight: 'min(520px, calc(100vh - 120px))',
          background: '#fff', borderRadius: 20,
          boxShadow: '0 20px 60px rgba(0,0,0,.15)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden', border: '1px solid #e5e7eb',
          fontFamily: 'Inter, -apple-system, sans-serif',
        }}>
          {/* Header */}
          <div style={{
            padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12,
            borderBottom: '1px solid #f3f4f6',
            background: 'var(--primary, #6f3df4)', color: '#fff',
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'rgba(255,255,255,.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, fontSize: 14,
            }}>CD</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>CoachDesk365</div>
              <div style={{ fontSize: 12, opacity: .8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', display: 'inline-block' }} />
                Jetzt erreichbar
              </div>
            </div>
          </div>

          {/* Messages */}
          <div ref={messagesRef} style={{
            flex: 1, overflowY: 'auto', padding: '16px 16px 8px',
            display: 'flex', flexDirection: 'column', gap: 10,
          }}>
            {messages.map((m, i) => (
              <div key={i} style={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '82%',
                padding: '10px 14px',
                borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                background: m.role === 'user' ? 'var(--primary, #6f3df4)' : '#f3f4f6',
                color: m.role === 'user' ? '#fff' : '#1f2937',
                fontSize: 14, lineHeight: 1.5,
              }}>
                {m.content}
              </div>
            ))}
            {loading && (
              <div style={{
                alignSelf: 'flex-start', padding: '10px 14px',
                borderRadius: '16px 16px 16px 4px', background: '#f3f4f6',
                fontSize: 14, color: '#9ca3af',
              }}>
                <span style={{ animation: 'pulse 1.5s infinite' }}>Schreibt...</span>
              </div>
            )}
          </div>

          {/* Quick Replies */}
          {showQuickReplies && (
            <div style={{ padding: '8px 16px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {QUICK_REPLIES.map((qr, i) => (
                <button key={i} onClick={() => sendMessage(qr.msg)} style={{
                  padding: '6px 12px', borderRadius: 20, border: '1px solid #e5e7eb',
                  background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  color: 'var(--primary, #6f3df4)', fontFamily: 'inherit',
                  transition: 'background .15s',
                }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                >
                  {qr.label}
                </button>
              ))}
            </div>
          )}

          {/* Email Capture */}
          {showEmailCapture && !emailSent && (
            <div style={{ padding: '10px 16px', background: '#faf5ff', borderTop: '1px solid #ede9fe' }}>
              <p style={{ fontSize: 12, color: '#6b21a8', margin: '0 0 8px', fontWeight: 600 }}>
                Sollen wir Sie persönlich kontaktieren?
              </p>
              <form onSubmit={(e) => {
                e.preventDefault();
                const emailInput = (e.target as HTMLFormElement).querySelector('input');
                if (emailInput?.value) {
                  setEmailSent(true);
                  setShowEmailCapture(false);
                  setMessages(prev => [...prev, { role: 'assistant', content: 'Vielen Dank! Wir melden uns bei Ihnen. In der Zwischenzeit können Sie CoachDesk365 auch direkt 14 Tage kostenlos testen.' }]);
                }
              }} style={{ display: 'flex', gap: 6 }}>
                <input type="email" placeholder="name@firma.de" required style={{
                  flex: 1, padding: '8px 12px', borderRadius: 10, border: '1px solid #e5e7eb',
                  fontSize: 13, fontFamily: 'inherit', outline: 'none',
                }} />
                <button type="submit" style={{
                  padding: '8px 14px', borderRadius: 10, border: 'none',
                  background: 'var(--primary, #6f3df4)', color: '#fff',
                  fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                }}>Senden</button>
              </form>
            </div>
          )}

          {/* Input */}
          <form onSubmit={handleSubmit} style={{
            padding: '12px 16px', borderTop: '1px solid #f3f4f6',
            display: 'flex', gap: 8, alignItems: 'center',
          }}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Ihre Frage..."
              disabled={loading}
              style={{
                flex: 1, padding: '10px 14px', borderRadius: 12,
                border: '1px solid #e5e7eb', fontSize: 14, fontFamily: 'inherit',
                outline: 'none', background: '#fafafa',
              }}
              onFocus={e => (e.target.style.borderColor = 'var(--primary, #6f3df4)')}
              onBlur={e => (e.target.style.borderColor = '#e5e7eb')}
            />
            <button type="submit" disabled={loading || !input.trim()} style={{
              width: 40, height: 40, borderRadius: 12, border: 'none',
              background: input.trim() ? 'var(--primary, #6f3df4)' : '#e5e7eb',
              color: '#fff', cursor: input.trim() ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background .15s',
            }}>
              <Send size={18} />
            </button>
          </form>
        </div>
      )}

      <style>{`@keyframes pulse { 0%, 100% { opacity: .4; } 50% { opacity: 1; } }`}</style>
    </>
  );
}
